import { useCallback, useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import { createAsyncAction, useAwaitControl } from 'react-redux-await-control';

const COOLDOWN_MS = 2000;

type AsyncAction = ReturnType<typeof createAsyncAction>;

/**
 * Controla o botão "Atualizar" da aba de Retornos.
 *
 * - Re-dispara apenas a action de retornos do contrato (mesmo actionId),
 *   sem afetar as demais abas.
 * - `loading` (chamada em andamento): botao exibe spinner e fica desabilitado.
 * - `disabled` cobre tambem o cooldown pos-sucesso, evitando cliques em sequencia.
 * - Trata sucesso/erro com toast, sem deixar o botao preso.
 */
export function useReturnsRefresh(action: AsyncAction, payload: Record<string, unknown>, actionId: string | number) {
  const control = useAwaitControl(action);
  const { start } = control;
  // isRunning/hasFailure usam useSelector internamente: precisam ser lidos
  // no corpo do hook, nunca dentro de effects/callbacks.
  const running = control.isRunning(actionId);
  const failed = control.hasFailure(actionId);

  const [cooldown, setCooldown] = useState(false);
  const triggeredRef = useRef(false);
  const prevRunningRef = useRef(running);

  // Detecta o fim de uma atualização disparada manualmente pelo botão.
  useEffect(() => {
    const wasRunning = prevRunningRef.current;
    prevRunningRef.current = running;

    if (!triggeredRef.current || !wasRunning || running) {
      return;
    }

    triggeredRef.current = false;

    if (failed) {
      message.error('Não foi possível atualizar os retornos. Tente novamente.');
      return;
    }

    message.success('Retornos atualizados');
    setCooldown(true);
  }, [running, failed, actionId]);

  // Libera o botão ao fim do cooldown.
  useEffect(() => {
    if (!cooldown) return undefined;
    const timer = setTimeout(() => setCooldown(false), COOLDOWN_MS);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const disabled = running || cooldown;

  const refresh = useCallback(() => {
    if (disabled) return;
    triggeredRef.current = true;
    start(payload, { actionId });
  }, [disabled, payload, actionId, start]);

  return { refresh, loading: running, disabled };
}
